/**
 * Board Governance — list of all meetings + create new.
 *
 * Route: board
 *
 * STAFF / board-INTERNAL screen. Shows the meeting list (any status), the
 * board members + their Class A shares, and a New Meeting button that
 * opens a create form. Per-meeting management (start/end/voting items/
 * minutes/results draft/publish) lives at board/:meetingId.
 *
 * Ported from the legacy publisher dashboard
 * (src/views/admin/BoardGovernance/index.jsx). The legacy view used the
 * dashboard's purple accent; admin.nurev.io uses amber. Status colors
 * stay semantic (emerald = in progress, amber = voting closed, etc.).
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Calendar,
  Plus,
  CheckCircle,
  Clock,
  AlertCircle,
  X,
  Video,
  type LucideIcon
} from 'lucide-react';
import {
  useBoardMeetings,
  useBoardMembers,
  useCreateMeeting
} from '@/hooks/useBoard';
import { BoardMeeting, MeetingStatus, MeetingType } from '@/api/board';
import { Card, SectionLabel, Skeleton, ActionButton } from '@/components/ui/atoms';

interface StatusConfig {
  label: string;
  className: string;
  icon: LucideIcon;
  pulse?: boolean;
}

const STATUS_DISPLAY: Record<MeetingStatus, StatusConfig> = {
  scheduled: {
    label: 'Scheduled',
    className: 'bg-sky-500/15 text-sky-300 ring-1 ring-sky-500/30',
    icon: Calendar
  },
  in_progress: {
    label: 'In Progress',
    className: 'bg-emerald-500/15 text-emerald-300 ring-1 ring-emerald-500/30',
    icon: Clock,
    pulse: true
  },
  ended: {
    label: 'Voting Closed',
    className: 'bg-amber-500/15 text-amber-300 ring-1 ring-amber-500/30',
    icon: AlertCircle
  },
  concluded: {
    label: 'Concluded',
    className: 'bg-white/10 text-nurev-muted ring-1 ring-white/10',
    icon: CheckCircle
  },
  cancelled: {
    label: 'Cancelled',
    className: 'bg-red-500/15 text-red-300 ring-1 ring-red-500/30',
    icon: X
  }
};

const TYPE_DISPLAY: Record<MeetingType, string> = {
  quarterly: 'Quarterly',
  emergency: 'Emergency',
  annual: 'Annual'
};

const fmtDateTime = (d?: string | null): string => {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-US', {
    timeZone: 'America/Los_Angeles',
    dateStyle: 'medium',
    timeStyle: 'short'
  });
};

export function BoardScreen() {
  const [creating, setCreating] = useState(false);
  const { data: meetingsData, isLoading } = useBoardMeetings();
  const { data: membersData } = useBoardMembers();

  const meetings = meetingsData?.meetings ?? [];
  const members = membersData?.members ?? [];

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-nurev-text">
            Board Governance
          </h1>
          <p className="mt-1 text-sm text-nurev-muted">
            Create board meetings, manage voting items, and publish results to
            shareholders.
          </p>
        </div>
        <ActionButton onClick={() => setCreating(true)}>
          <span className="inline-flex items-center gap-2">
            <Plus className="h-3.5 w-3.5" />
            New meeting
          </span>
        </ActionButton>
      </div>

      {/* Board members panel */}
      <Card>
        <SectionLabel>Board members ({members.length})</SectionLabel>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
          {members.map(m => (
            <div
              key={m._id}
              className="flex items-center justify-between gap-3 rounded-lg border border-nurev-border bg-nurev-surface-lift px-3 py-2.5"
            >
              <div>
                <div className="text-sm font-semibold text-nurev-text">
                  {m.name}
                </div>
                <div className="text-[11px] text-nurev-muted">{m.email}</div>
              </div>
              <div className="text-right">
                <div className="text-sm font-bold text-amber-300">
                  {m.classAShares}
                </div>
                <div className="text-[10px] uppercase tracking-wider text-nurev-muted">
                  {m.boardRole === 'chair' ? 'Chair' : 'Member'}
                </div>
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Meetings list */}
      <Card>
        <SectionLabel>Meetings</SectionLabel>
        {isLoading && (
          <div className="space-y-2">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        )}
        {!isLoading && meetings.length === 0 && (
          <div className="py-8 text-center text-sm text-nurev-muted">
            No meetings yet. Click <strong>New meeting</strong> to create one.
          </div>
        )}
        {!isLoading && meetings.length > 0 && (
          <div className="space-y-2">
            {meetings.map(m => (
              <MeetingRow key={m._id} meeting={m} />
            ))}
          </div>
        )}
      </Card>

      {creating && <NewMeetingModal onClose={() => setCreating(false)} />}
    </div>
  );
}

function MeetingRow({ meeting }: { meeting: BoardMeeting }) {
  const cfg = STATUS_DISPLAY[meeting.status] ?? STATUS_DISPLAY.scheduled;
  const Icon = cfg.icon;
  return (
    <Link
      to={`/board/${meeting._id}`}
      className="flex items-center justify-between gap-4 rounded-lg border border-nurev-border bg-transparent px-4 py-3 text-left transition-colors hover:border-amber-500/40 hover:bg-white/[0.03]"
    >
      <div className="flex items-center gap-3">
        <div
          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${cfg.className}`}
        >
          <Icon className="h-4 w-4" />
        </div>
        <div>
          <div className="text-sm font-semibold text-nurev-text">
            {meeting.name}
          </div>
          <div className="text-[11px] text-nurev-muted">
            {TYPE_DISPLAY[meeting.meetingType] ?? 'Quarterly'} ·{' '}
            {fmtDateTime(meeting.scheduledAt)}
            {meeting.recordingUrl && (
              <span className="ml-2 inline-flex items-center gap-0.5 text-emerald-300">
                <Video className="h-2.5 w-2.5" /> Recording
              </span>
            )}
          </div>
        </div>
      </div>
      <span
        className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider ${cfg.className}`}
      >
        {cfg.pulse && (
          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-400" />
        )}
        {cfg.label}
      </span>
    </Link>
  );
}

// ─── New meeting modal ────────────────────────────────────────────────────────

function NewMeetingModal({ onClose }: { onClose: () => void }) {
  const create = useCreateMeeting();
  const [form, setForm] = useState({
    name: '',
    meetingType: 'quarterly' as MeetingType,
    description: '',
    scheduledAt: '',
    executiveSession: false
  });

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name || !form.scheduledAt) return;
    try {
      await create.mutateAsync({
        name: form.name,
        meetingType: form.meetingType,
        description: form.description,
        executiveSession: form.executiveSession,
        scheduledAt: new Date(form.scheduledAt).toISOString()
      });
      onClose();
    } catch {
      // error surfaced via mutation state below
    }
  };

  const types: MeetingType[] = ['quarterly', 'emergency', 'annual'];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg space-y-4 rounded-xl border border-nurev-border bg-nurev-surface-lift p-6"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-nurev-text">
            New board meeting
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-nurev-muted hover:text-nurev-text"
          >
            <X className="h-[18px] w-[18px]" />
          </button>
        </div>
        <form onSubmit={onSubmit} className="space-y-4">
          <FormField
            label="Meeting name"
            value={form.name}
            onChange={v => setForm(f => ({ ...f, name: v }))}
            placeholder="e.g. Q1 2026 Board Meeting"
            required
          />
          <div>
            <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-wider text-nurev-muted">
              Meeting type
            </label>
            <div className="grid grid-cols-3 gap-2">
              {types.map(t => {
                const selected = form.meetingType === t;
                return (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setForm(f => ({ ...f, meetingType: t }))}
                    className={`rounded-lg border px-3 py-2 text-xs capitalize transition-all ${
                      selected
                        ? 'border-amber-500/65 bg-amber-500/10 font-semibold text-amber-300'
                        : 'border-nurev-border text-nurev-text-soft'
                    }`}
                  >
                    {t}
                  </button>
                );
              })}
            </div>
          </div>
          <FormField
            label="Scheduled date + time"
            type="datetime-local"
            value={form.scheduledAt}
            onChange={v => setForm(f => ({ ...f, scheduledAt: v }))}
            required
          />
          <div>
            <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-wider text-nurev-muted">
              Description (optional)
            </label>
            <textarea
              value={form.description}
              onChange={e =>
                setForm(f => ({ ...f, description: e.target.value }))
              }
              rows={3}
              placeholder="Agenda preview / context"
              className="w-full resize-none rounded-lg border border-nurev-border bg-white/[0.04] px-3.5 py-2.5 text-sm text-nurev-text outline-none focus:border-amber-500/50"
            />
          </div>
          <label className="flex cursor-pointer items-center gap-2 text-xs text-nurev-muted">
            <input
              type="checkbox"
              checked={form.executiveSession}
              onChange={e =>
                setForm(f => ({ ...f, executiveSession: e.target.checked }))
              }
            />
            Executive session (minutes restricted to board members)
          </label>
          {create.isError && (
            <div className="text-xs text-red-300">
              Failed to create meeting. Please try again.
            </div>
          )}
          <div className="flex items-center justify-end gap-2 pt-2">
            <ActionButton variant="neutral" onClick={onClose}>
              Cancel
            </ActionButton>
            <ActionButton
              type="submit"
              disabled={create.isPending || !form.name || !form.scheduledAt}
            >
              {create.isPending ? 'Creating…' : 'Create meeting'}
            </ActionButton>
          </div>
        </form>
      </div>
    </div>
  );
}

function FormField({
  label,
  value,
  onChange,
  type = 'text',
  placeholder,
  required
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
  required?: boolean;
}) {
  return (
    <div>
      <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-wider text-nurev-muted">
        {label}
        {required && <span className="ml-1 text-red-400">*</span>}
      </label>
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        className="w-full rounded-lg border border-nurev-border bg-white/[0.04] px-3.5 py-2.5 text-sm text-nurev-text outline-none focus:border-amber-500/50"
      />
    </div>
  );
}
